import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import Divider from '@material-ui/core/Divider'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#EB5757',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    dialogCont: {
        color: theme.palette.common.black,
        '@media (max-width: sm)': {
            width: 250,
        },
        '&.MuiDialogContent-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
        height: 150,
        fontSize: pxToRem(18),
    },
    dialogContSmall: {
        '&.MuiDialogContent-root': {
            width: pxToRem(500),
            height: pxToRem(100),
        },
    },
    actionButton: {
        color: '#A78966',
        fontSize: pxToRem(18),
        width: 108,
        marginBottom: 16,
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(18),
        color: theme.palette.common.black,
    },
    warningDialogCont: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
}))

export interface ConfirmationDialogProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose?: VoidFunction

    /**
     * Trigger action on confirmation click
     */
    dialogActionCallback: (_: React.MouseEvent<Element, MouseEvent>) => void

    /**
     * Defines the heading of the confirmation dialog. Default: Confirm
     */
    dialogHeading?: string

    /**
     * Defines the content for the confirmation dialog.
     */
    contentNode?: React.ReactNode

    /*
     * Defines the confirmation message placed above action buttons on the confirmation dialog. Default: Are you sure you want to proceed?
     */
    confirmMessage?: string

    /*
     * Defines the text for cancel action button on confirmation dialog. Default: Cancel
     */
    cancelText?: string

    /*
     * Defines the text for confirmation action button  on confirmation dialog. Default: Ok
     */
    confirmText?: string
    /*
     * Defines the size for confirmation dialog. Default: default
     */
    size?: 'small' | 'default'
}
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
    openDialog,
    handleDialogClose,
    dialogActionCallback,
    dialogHeading = 'Confirm Remove',
    contentNode,
    confirmMessage = 'Are you sure you want to proceed?',
    cancelText = 'Cancel',
    confirmText = 'Remove',
    size,
}) => {
    const classes = useStyles()

    return (
        <Dialog
            open={openDialog}
            data-testid="confirmationDialog"
            className={classes.warningDialogCont}
            aria-labelledby="confirmation-action"
        >
            <DialogTitle className={classes.dialogTitle}>
                {dialogHeading}
            </DialogTitle>
            <DialogContent
                className={clsx(
                    classes.dialogCont,
                    size === 'small' && classes.dialogContSmall
                )}
            >
                {contentNode}
            </DialogContent>
            <Divider variant="fullWidth" />
            <Typography className={classes.confirmationText}>
                {confirmMessage}
            </Typography>
            <DialogActions className={classes.dialogAction}>
                {cancelText ? (
                    <Button
                        data-testid="cancelButton"
                        className={classes.actionButton}
                        onClick={handleDialogClose}
                    >
                        {cancelText}
                    </Button>
                ) : (
                    <></>
                )}
                <Button
                    data-testid="confirmButton"
                    onClick={dialogActionCallback}
                    className={classes.actionButton}
                >
                    {confirmText}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ConfirmationDialog
